import React, { useState, useContext } from 'react';
import { Box, Typography, List, ListItem, Button } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import subscriptionPlans from '../authenticated/settings/subscriptionPlans';
import { useLocation } from 'react-router-dom';
import ConfirmationDialog from './ConfirmationDialog';
import StoreContext from '../../react-core-lib/components/app/store-context';
import UserApi from '../../rest-api/user-api';
import KlaviyoApi from '../../rest-api/klaviyo-api';
import BriefcaseIcon from '../../assets/icons/Briefcase.svg';
import ClinicIcon from '../../assets/icons/Clinic.svg';

const SubscriptionCard = ({
    plan,
    discount = null, // Can be {type: 'percent', value: X} or {type: 'amount', value: Y, currency: 'gbp'}
    isCurrentPlan,
    billingCycle,
    showSelect = false,
    sx,
    disabled = false,
    isExpo = false,
    onSelectPlan,
}) => {
    console.log('discount found ', discount);

    const planDetails = subscriptionPlans[plan];
    const location = useLocation();
    const [showTrialDialog, setShowTrialDialog] = useState(false);
    const { authStore, userDataStore } = useContext(StoreContext);

    if (!planDetails) {
        return <Typography variant="body1">Invalid plan selected.</Typography>;
    }

    const { priceMonthly, priceAnnually, specialPriceMonthly, specialPriceAnnually, benefits, description } =
        planDetails;
    const isSettingsManageSubscription = location.pathname.endsWith('/settings/manage-subscription');
    const canSelectPlan = !disabled && !isCurrentPlan;
    const displayName = plan === 'Professional' ? 'Pro' : plan;
    const PlanIcon = {
        Free: LockOpenOutlinedIcon,
    }[plan];
    const isProfessionalPlan = plan === 'Professional';
    const isClinicPlan = plan === 'Clinic';

    const discountedPrice = (price, discount, isExpo, specialPrice) => {
        if (isExpo && specialPrice) {
            return specialPrice.toFixed(2);
        }

        if (!discount) {
            return price.toFixed(2);
        }

        // Handle percentage discounts
        if (discount.type === 'percent') {
            return (price * (1 - discount.value / 100)).toFixed(2);
        }

        // Handle fixed amount discounts (ensure price doesn't go below 0)
        if (discount.type === 'amount') {
            return Math.max(0, price - discount.value).toFixed(2);
        }

        return price.toFixed(2);
    };

    const onConfirmTrial = async () => {
        const trialEndDate = new Date();
        trialEndDate.setDate(trialEndDate.getDate() + 14);
        const trial_end = Math.floor(trialEndDate.getTime());

        const subscriptionData = {
            accountType: 'Professional',
            trialActive: true,
            trialEndDate: trial_end,
            subscriptionEndDate: trial_end,
            cancelled: false,
        };

        await UserApi.updateClinicianData(authStore.idToken, { subscriptionData });

        // Update userDataStore with the new subscription details
        userDataStore.setUserData({
            ...userDataStore.getUserData(),
            subscriptionData,
        });
        localStorage.setItem('trialSuccess', 'true');
        authStore.checkAndSetAuthState(userDataStore.userData.emailAddress, authStore.idToken);
        await KlaviyoApi.updateUserInKlaviyo(
            authStore.idToken,
            userDataStore.userData.emailAddress,
            'Pro Plan Trial 14d',
        );
    };

    const handleCardClick = (e) => {
        if (!canSelectPlan) {
            e.stopPropagation(); // prevent click bubbling
            return;
        }

        if (isSettingsManageSubscription && onSelectPlan) {
            onSelectPlan(plan);
            return;
        }

        if (showSelect) {
            setShowTrialDialog(true);
        }
    };

    const handleClose = (e) => {
        if (e) {
            e.stopPropagation();
        }
        setShowTrialDialog(false);
    };

    return (
        <Box
            onClick={handleCardClick}
            sx={{
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                borderRadius: '30px',
                border: '0.5px solid #e5e5e5',
                overflow: 'hidden',
                height: '100%',
                ...sx,
            }}
        >
            <Box
                className="settings-block"
                sx={{
                    backgroundColor: 'white',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                }}
            >
                <Box
                    sx={{
                        backgroundColor: '#f0f0fc',
                        p: 3,
                        pb: 2.5,
                    }}
                >
                    {isCurrentPlan && isSettingsManageSubscription && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 20,
                                right: 20,
                                width: 120,
                                height: 40,
                                borderRadius: '100px',
                                border: '2px solid #FA96C3',
                                backgroundColor: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    lineHeight: '14px',
                                    letterSpacing: '-0.28px',
                                    backgroundImage:
                                        'linear-gradient(135deg, #FA96C3 -12.61%, #FF669B 4.47%, #BD8DE8 100.7%)',
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                }}
                            >
                                Current plan
                            </Box>
                        </Box>
                    )}

                    {(PlanIcon || isProfessionalPlan || isClinicPlan) && (
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '12px',
                                border: '1px solid rgba(255, 102, 155, 0.35)',
                                bgcolor: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 2,
                            }}
                        >
                            {isProfessionalPlan ? (
                                <Box
                                    component="img"
                                    src={BriefcaseIcon}
                                    alt="Briefcase"
                                    sx={{ width: 20, height: 20 }}
                                />
                            ) : isClinicPlan ? (
                                <Box component="img" src={ClinicIcon} alt="Clinic" sx={{ width: 20, height: 22 }} />
                            ) : (
                                <PlanIcon sx={{ color: '#FF669B', fontSize: 26 }} />
                            )}
                        </Box>
                    )}

                    <Typography
                        sx={{
                            fontSize: { xs: '32px', md: '34px' },
                            fontWeight: 600,
                            color: '#000453',
                            letterSpacing: { xs: '-1.28px', md: '-0.7px' },
                            lineHeight: { xs: '32px', md: 1.1 },
                            mb: 1,
                        }}
                    >
                        {displayName}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: { xs: '12px', md: '14px' },
                            color: '#616063',
                            lineHeight: { xs: '16px', md: 1.5 },
                            letterSpacing: { xs: '-0.24px', md: 'normal' },
                            mb: 2,
                            minHeight: { xs: 'auto', md: 42 },
                        }}
                    >
                        {description || ''}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px', mb: 0.5 }}>
                        <Typography
                            sx={{
                                fontSize: { xs: '24px', md: '32px' },
                                fontWeight: 600,
                                color: '#000453',
                                letterSpacing: { xs: '-0.96px', md: '-0.6px' },
                                lineHeight: { xs: '24px', md: 'normal' },
                            }}
                        >
                            £
                            {billingCycle === 'monthly'
                                ? discountedPrice(priceMonthly, discount, false, specialPriceMonthly)
                                : discountedPrice(priceAnnually, discount, isExpo, specialPriceAnnually)}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: { xs: '12px', md: '14px' },
                                color: '#616063',
                                fontWeight: 400,
                                letterSpacing: { xs: '-0.48px', md: 'normal' },
                            }}
                        >
                            {billingCycle === 'monthly' ? '/month' : '/year'}
                        </Typography>
                    </Box>
                </Box>

                <Box
                    sx={{
                        px: { xs: 3, md: 3 },
                        pt: { xs: 3, md: 3 },
                        pb: { xs: 3, md: 3 },
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                    }}
                >
                    <List sx={{ p: 0, pb: 0, flexGrow: 1, mb: 3 }}>
                        {benefits.map((benefit, index) => {
                            const isBold = benefit.startsWith('**') && benefit.endsWith('**');
                            const text = isBold ? benefit.slice(2, -2) : benefit;

                            return (
                                <ListItem
                                    key={index}
                                    sx={{
                                        p: 0,
                                        pb: 1,
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '8px',
                                    }}
                                >
                                    <CheckCircleOutlineIcon sx={{ color: '#FF669B', fontSize: 18, mt: '1px' }} />
                                    <Typography
                                        sx={{
                                            fontSize: '13px',
                                            color: '#333',
                                            lineHeight: 1.5,
                                            fontWeight: isBold ? 700 : 400,
                                        }}
                                    >
                                        {text}
                                    </Typography>
                                </ListItem>
                            );
                        })}
                    </List>

                    {isSettingsManageSubscription && (
                        <Button
                            fullWidth
                            onClick={(e) => {
                                e.stopPropagation();
                                if (canSelectPlan && onSelectPlan) {
                                    onSelectPlan(plan);
                                }
                            }}
                            disabled={!canSelectPlan}
                            sx={{
                                borderRadius: '18.397px',
                                height: { xs: '30.75px', md: 37 },
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '14px',
                                boxShadow: 'none',
                                backgroundColor: isCurrentPlan ? '#E6E6E6' : '#FF669B',
                                color: '#fff',
                                '&:hover': {
                                    backgroundColor: isCurrentPlan ? '#E6E6E6' : '#e6568b',
                                    boxShadow: 'none',
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: isCurrentPlan ? '#E6E6E6' : 'rgba(255,102,155,0.4)',
                                    color: '#fff',
                                },
                            }}
                        >
                            Select plan
                        </Button>
                    )}
                </Box>

                {showSelect && !isSettingsManageSubscription && (
                    <Button onClick={() => setShowTrialDialog(true)} sx={{ mb: 0 }}>
                        Select
                    </Button>
                )}
            </Box>
            {/* Existing Trial Confirmation Dialog */}
            <ConfirmationDialog
                open={showTrialDialog}
                handleClose={handleClose}
                onConfirm={onConfirmTrial}
                title="Great choice! Let's get started!"
                message="Rest assured we won't take any payment now. We will remind you 7 days before your trial ends."
                confirmText="Start trial now"
                cancelText="No, go back"
            />
        </Box>
    );
};

export default SubscriptionCard;
